import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa uma divisão de business unit
 * 
 * @typedef {Object} BusinessUnitDivisao
 * @property {number} id - Identificador único
 * @property {string} nome - Nome da divisão
 */

/**
 * Representa o payload de criação/atualização de uma divisão de business unit
 * 
 * @typedef {Object} BusinessUnitDivisaoPayload
 * @property {string} nome - Nome da divisão (obrigatório, único por empresa)
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/business-units-divisao`,
  item: (empresaId, divisaoId) => `v1/ativacaoped/${empresaId}/business-units-divisao/${divisaoId}`
};

const LOADING_KEY = 'ativacaoped-business-units-divisao';

/**
 * Lista divisões de business units com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object|string} [params] - Parâmetros de consulta (objeto) ou query string já montada
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<BusinessUnitDivisao>>} Lista paginada de divisões
 */
export const getBusinessUnitsDivisao = (empresaId, params = { page: 1, per_page: 10 }) => {
  // Aceita a query string crua montada pelo MasterDetail (page/per_page/sort/filters).
  const query = typeof params === "string" ? params : parseParams(params);
  let url = resource.base(empresaId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Lista divisões de business units simplificadas para componentes de seleção (dropdown, autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<BusinessUnitDivisao[]>} Lista de divisões
 */
export const getSelecaoBusinessUnitsDivisao = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Cria uma nova divisão de business unit.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {BusinessUnitDivisaoPayload} data - Dados da divisão a ser criada
 * @returns {Promise<BusinessUnitDivisao>} Divisão criada
 */
export const postBusinessUnitDivisao = (empresaId, data) => {
  const baseUrl = resource.base(empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Atualiza uma divisão de business unit existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} divisaoId - ID da divisão a ser atualizada
 * @param {BusinessUnitDivisaoPayload} data - Dados parciais da divisão para atualização
 * @returns {Promise<BusinessUnitDivisao>} Divisão atualizada
 */
export const putBusinessUnitDivisao = (empresaId, divisaoId, data) => {
  const baseUrl = resource.item(empresaId, divisaoId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui uma divisão de business unit.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} divisaoId - ID da divisão a ser excluída
 * @returns {Promise<void>}
 */
export const deleteBusinessUnitDivisao = (empresaId, divisaoId) => {
  const baseUrl = resource.item(empresaId, divisaoId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

export default {
  getBusinessUnitsDivisao,
  getSelecaoBusinessUnitsDivisao,
  postBusinessUnitDivisao,
  putBusinessUnitDivisao,
  deleteBusinessUnitDivisao
};